import { ApiService } from '~/services/api/api'
import type {
  OrganizationDetail,
  OrganizationListItem,
  OrganizationSummary,
  OrganizationUserDetailEntry,
  OrganizationUserEntry,
  OrganizationWorkspaceSummary,
} from '~/types/Organization'
import type { PaginatedResponse } from '~/types/PaginatedResponse'
import type { UserInviteResponse } from '~/types/Invite'
import type { Workspace } from '~/types/Workspace'
import type {
  MoveWorkspacesConfirmRequest,
  MoveWorkspacesConfirmResult,
  MoveWorkspacesReviewRequest,
  MoveWorkspacesReviewResult,
} from '~/types/MoveWorkspaces'

export class OrganizationService {
  private readonly apiService = new ApiService()

  // System-admin all-organisations list — every org with per-org workspace count,
  // owner display name, and created date. Also powers the move-workspaces
  // destination picker.
  async getAllOrganizations() {
    return await this.apiService.get<OrganizationListItem[]>('/organizations')
  }

  // Resolve a single org by slug (GET /organizations/by-slug/:slug). Used to
  // resolve the current org for super users viewing an org they don't belong to
  // (absent from GET /users/me), where the membership list can't supply it.
  async getOrganizationBySlug(slug: string) {
    return await this.apiService.get<OrganizationDetail>(
      `/organizations/by-slug/${slug}`,
    )
  }

  // All workspaces in the organization with per-workspace member counts —
  // unlike the /me store's list, this is org-wide (not the caller's own
  // memberships), which the super-user move flow needs.
  async getOrganizationWorkspaces(organizationId: string) {
    return await this.apiService.get<OrganizationWorkspaceSummary[]>(
      `/organizations/${organizationId}/workspaces`,
    )
  }

  // Paginated + searchable org members (each a UserSummary + membership row).
  // Backs the workspace "Add users" typeahead: `query` matches
  // name/email server-side, `limit` caps the page for a lightweight suggest.
  async getOrganizationUsers(
    organizationId: string,
    params: { query?: string; limit?: number } = {},
  ) {
    const response = await this.apiService.get<
      PaginatedResponse<OrganizationUserEntry>
    >(`/organizations/${organizationId}/users`, {
      params: {
        limit: String(params.limit ?? 20),
        ...(params.query ? { query: params.query } : {}),
      },
    })
    if (response.error) {
      return { data: null, error: response.error }
    }
    return { data: response.data?.data ?? [], error: null }
  }

  // A single org member as a full detail entry: the user (UserDetail) plus their
  // org membership with workspaces-in-org inline. 404 when the user holds no
  // membership in this org. Backs the org user detail page.
  async getOrganizationUserDetail(organizationId: string, userId: string) {
    return await this.apiService.get<OrganizationUserDetailEntry>(
      `/organizations/${organizationId}/users/${userId}`,
    )
  }

  // Non-destructive review of a workspace move: returns the members and invites
  // the move would affect, changing nothing. The org in the path is the SOURCE.
  async reviewMoveWorkspaces(
    organizationId: string,
    body: MoveWorkspacesReviewRequest,
  ) {
    return await this.apiService.post<
      MoveWorkspacesReviewResult,
      MoveWorkspacesReviewRequest
    >(`/organizations/${organizationId}/move-workspaces/review`, body)
  }

  // Transactional move. `userIdsToMove` ∪ `userIdsToExclude` must exactly equal
  // the review's affected-user set (and be disjoint) or the API returns 409
  // REVIEW_MISMATCH.
  async confirmMoveWorkspaces(
    organizationId: string,
    body: MoveWorkspacesConfirmRequest,
  ) {
    return await this.apiService.post<
      MoveWorkspacesConfirmResult,
      MoveWorkspacesConfirmRequest
    >(`/organizations/${organizationId}/move-workspaces/confirm`, body)
  }

  // Creates an organisation; the caller becomes owner + member (server-side,
  // from the auth token). Only `name` is required. NOTE: this does NOT create a
  // default workspace — the design's "default workspace" field is unbacked
  // today, so the system-admin modal creates the org only (see TRV-946 gap).
  async createOrganization(name: string) {
    return await this.apiService.post<OrganizationDetail, { name: string }>(
      '/organizations',
      { name },
    )
  }

  // System-admin "join organisation" super action — adds a single (super) user
  // to the org. The API enforces isSuper (only supers may belong to more than
  // one org) and returns the created/existing membership.
  async addOrganizationMembership(organizationId: string, userId: string) {
    return await this.apiService.post<{ id: string }, { userId: string }>(
      `/organizations/${organizationId}/memberships`,
      { userId },
    )
  }

  // Creates a workspace inside the organization. The API accepts only `title`
  // today (no description column); the org is scoped via the path param.
  async createWorkspace(organizationId: string, title: string) {
    return await this.apiService.post<Workspace, { title: string }>(
      `/organizations/${organizationId}/workspaces`,
      { title },
    )
  }

  // Settings Summary tab — all four metric cards in one call. Counts are
  // server-derived and scoped to live records (no client-side deriving).
  async getOrganizationSummary(organizationId: string) {
    return await this.apiService.get<OrganizationSummary>(
      `/organizations/${organizationId}/summary`,
    )
  }

  // Returns ALL org invites (accepted / revoked / expired / pending mixed) —
  // callers derive "pending" from acceptedAt / revokedAt / expiresAt.
  async getOrganizationInvites(organizationId: string) {
    return await this.apiService.get<UserInviteResponse[]>(
      `/organizations/${organizationId}/invites`,
    )
  }

  // Only `name` is mutable here; slug and owner have dedicated endpoints.
  async updateOrganizationById(
    organizationId: string,
    body: { name?: string },
  ) {
    return await this.apiService.patch<OrganizationDetail, { name?: string }>(
      `/organizations/${organizationId}`,
      body,
    )
  }

  // Multipart upload — builds its own FormData. Accepts jpeg/png/webp/gif up
  // to 5MB (enforced API-side).
  async uploadOrganizationLogo(organizationId: string, file: File) {
    const formData = new FormData()
    formData.append('file', file, file.name)
    return await this.apiService.post<{ logoUrl: string }, FormData>(
      `/organizations/${organizationId}/logo`,
      formData,
    )
  }

  async deleteOrganizationLogo(organizationId: string) {
    return await this.apiService.delete<{ logoUrl: null }>(
      `/organizations/${organizationId}/logo`,
    )
  }
}
